// FriendsPage — 好友列表 + 好友请求 + 添加好友

import { useState, useEffect, useCallback } from 'react';
import type { Friendship, FriendRequest } from '@kuaichuan/shared';
import {
  getFriends,
  getFriendRequests,
  addFriend,
  acceptFriend,
  rejectFriend,
  removeFriend,
} from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { useToastStore } from '../common/Toast';

interface FriendsPageProps {
  onChatWith: (friend: Friendship) => void;
}

export function FriendsPage({ onChatWith }: FriendsPageProps) {
  const token = useAuthStore((s) => s.token)!;
  const toast = useToastStore((s) => s.show);
  const [friends, setFriends] = useState<Friendship[]>([]);
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [fl, rl] = await Promise.all([getFriends(token), getFriendRequests(token)]);
      setFriends(fl);
      setRequests(rl);
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAccept = async (req: FriendRequest) => {
    try {
      const r = await acceptFriend(token, {
        requestId: req.id,
        fromId: req.fromId,
        fromEmail: req.fromEmail,
        fromName: req.fromName,
      });
      if (r.success) {
        toast('好友添加成功', 'success');
        refresh();
      } else {
        toast(r.message ?? '操作失败', 'error');
      }
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };

  const handleReject = async (req: FriendRequest) => {
    try {
      await rejectFriend(token, req.id);
      refresh();
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };

  const handleRemove = async (friendId: string) => {
    if (!confirm('确定删除该好友？')) return;
    try {
      await removeFriend(token, friendId);
      toast('已删除好友', 'success');
      refresh();
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      {/* 顶部 */}
      <div className="flex items-center justify-between px-4 py-4">
        <h2 className="text-xl font-bold text-ink">好友</h2>
        <button type="button" onClick={() => setShowAdd(true)} className="btn-primary px-4 py-2 text-sm">
          ＋ 添加
        </button>
      </div>

      {/* 好友请求 */}
      {requests.length > 0 && (
        <div className="px-4 mb-2">
          <div className="text-xs text-muted mb-2">好友请求（{requests.length}）</div>
          {requests.map((req) => (
            <div key={req.id} className="card p-3 mb-2 flex items-center gap-3">
              <Avatar name={req.fromName} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-ink truncate">{req.fromName}</div>
                <div className="text-xs text-muted truncate">{req.fromEmail}</div>
                {req.message && <div className="text-xs text-muted mt-0.5">验证消息：{req.message}</div>}
              </div>
              <button type="button" onClick={() => handleAccept(req)} className="btn-primary px-3 py-1.5 text-xs">接受</button>
              <button type="button" onClick={() => handleReject(req)} className="btn-ghost px-3 py-1.5 text-xs">拒绝</button>
            </div>
          ))}
        </div>
      )}

      {/* 好友列表 */}
      <div className="px-4">
        {loading ? (
          <div className="text-center text-sm text-muted py-8">加载中…</div>
        ) : friends.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-3">👥</div>
            <p className="text-sm text-muted">还没有好友</p>
            <p className="text-xs text-muted mt-1">点击右上角"添加"搜索好友</p>
          </div>
        ) : (
          <>
            <div className="text-xs text-muted mb-2">好友（{friends.length}）</div>
            {friends.map((f) => (
              <div
                key={f.friendId}
                className="card p-3 mb-2 flex items-center gap-3 cursor-pointer hover:bg-bg transition-colors group"
                onClick={() => onChatWith(f)}
              >
                <Avatar name={f.friendName} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink truncate">{f.friendName}</div>
                  <div className="text-xs text-muted truncate">{f.friendEmail}</div>
                </div>
                <span className="text-muted group-hover:hidden">💬</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleRemove(f.friendId); }}
                  className="hidden group-hover:block text-xs text-danger"
                >
                  删除
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      {/* 添加好友对话框 */}
      {showAdd && (
        <AddFriendDialog
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); refresh(); }}
        />
      )}
    </div>
  );
}

// 添加好友对话框
function AddFriendDialog({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const token = useAuthStore((s) => s.token)!;
  const toast = useToastStore((s) => s.show);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!email.trim()) return toast('请输入好友邮箱', 'warning');
    setLoading(true);
    try {
      const r = await addFriend(token, { email: email.trim(), message: message.trim() || undefined });
      if (r.success) {
        toast(r.message ?? '请求已发送', 'success');
        onAdded();
      } else {
        toast(r.message ?? '添加失败', 'error');
      }
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="card w-full max-w-sm p-6">
        <h3 className="text-lg font-bold text-ink mb-4">添加好友</h3>
        <div className="space-y-3">
          <input
            className="input"
            type="email"
            placeholder="好友邮箱"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
          <input
            className="input"
            type="text"
            placeholder="验证消息（可选）"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={100}
          />
          <button type="button" onClick={submit} disabled={loading} className="btn-primary w-full">
            {loading ? '发送中…' : '发送好友请求'}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost w-full">取消</button>
        </div>
      </div>
    </div>
  );
}

// 头像
function Avatar({ name }: { name: string }) {
  const initial = (name || '?')[0].toUpperCase();
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0"
      style={{ background: 'linear-gradient(135deg,#6b7280,#9ca3af)' }}
    >
      {initial}
    </div>
  );
}
